import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Send, MoreHorizontal, Copy } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, formatApiError } from "@/lib/api";
import { StatusPill, EmptyState, TableWrap, th, td, fmtDate } from "@/components/dashboard/ui";

export default function AdminInvitations({ onChange }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", role: "artist", display_name: "", message: "" });
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/invitations");
      setInvites(data);
    } catch (e) {
      toast.error("Failed to load invitations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      toast.error("Enter an email address");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/invitations", { ...form, app_url: window.location.origin });
      toast.success(data.email_sent ? `Invitation emailed to ${form.email}` : `Invitation created for ${form.email} (email pending)`);
      setForm({ email: "", role: "artist", display_name: "", message: "" });
      load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err, "Failed to send invitation"));
    } finally {
      setSending(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/admin/invitations/${id}`);
      toast.success("Invitation revoked");
      load();
      onChange && onChange();
    } catch (err) {
      toast.error("Failed to revoke");
    }
  };

  const copyEmail = (email) => {
    navigator.clipboard?.writeText(email);
    toast.success("Email copied");
  };

  const inputCls = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder:text-slate-500";

  return (
    <div className="space-y-8" data-testid="admin-invitations">
      <form onSubmit={send} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="invite-form">
        <h3 className="font-display text-lg text-white mb-4">Invite an artist or label</h3>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <input
            data-testid="invite-email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="artist@email.com"
            type="email"
            className={`md:col-span-4 ${inputCls}`}
          />
          <input
            data-testid="invite-name"
            value={form.display_name}
            onChange={(e) => setForm((f) => ({ ...f, display_name: e.target.value }))}
            placeholder="Display name (optional)"
            className={`md:col-span-4 ${inputCls}`}
          />
          <div className="md:col-span-2">
            <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
              <SelectTrigger data-testid="invite-role" className="bg-white/5 border-white/10 text-white h-[46px] rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0b1220] border-white/10 text-white">
                <SelectItem value="artist">Artist</SelectItem>
                <SelectItem value="label">Label</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <button
            data-testid="invite-submit"
            type="submit"
            disabled={sending}
            className="md:col-span-2 inline-flex items-center justify-center gap-2 rounded-xl bg-white text-black font-medium px-5 h-[46px] hover:bg-slate-200 active:scale-[0.98] transition-all disabled:opacity-60"
          >
            {sending ? <Loader2 size={18} className="animate-spin" /> : <><Send size={16} /> Invite</>}
          </button>
        </div>
        <textarea
          data-testid="invite-message"
          value={form.message}
          onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
          placeholder="Add a personal note to the invite email (optional)"
          rows={2}
          className="mt-3 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder:text-slate-500 resize-none"
        />
      </form>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div>
      ) : invites.length === 0 ? (
        <EmptyState title="No invitations yet" desc="Send your first invite above to start building your roster." />
      ) : (
        <TableWrap>
          <thead>
            <tr>
              <th className={th}>Email</th>
              <th className={th}>Name</th>
              <th className={th}>Role</th>
              <th className={th}>Status</th>
              <th className={th}>Sent</th>
              <th className={th}></th>
            </tr>
          </thead>
          <tbody>
            {invites.map((inv) => (
              <tr key={inv.invitation_id} data-testid={`invite-row-${inv.invitation_id}`} className="hover:bg-white/[0.02]">
                <td className={td + " text-white"}>{inv.email}</td>
                <td className={td}>{inv.display_name}</td>
                <td className={td + " capitalize"}>{inv.role}</td>
                <td className={td}><StatusPill status={inv.status} /></td>
                <td className={td}>{fmtDate(inv.created_at)}</td>
                <td className={td + " text-right"}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`invite-actions-${inv.invitation_id}`} className="p-2 rounded-lg hover:bg-white/10 text-slate-400">
                        <MoreHorizontal size={18} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#0b1220] border-white/10 text-white">
                      <DropdownMenuItem onClick={() => copyEmail(inv.email)} className="focus:bg-white/10 cursor-pointer">
                        <Copy size={15} className="mr-2" /> Copy email
                      </DropdownMenuItem>
                      {inv.status === "pending" && (
                        <DropdownMenuItem onClick={() => revoke(inv.invitation_id)} className="text-red-400 focus:bg-white/10 focus:text-red-400 cursor-pointer" data-testid={`invite-revoke-${inv.invitation_id}`}>
                          Revoke invitation
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </div>
  );
}
